import React, { useMemo } from "react";
import { Formik, Form, Field } from "formik";
import CallCenterModal from "./listing/CallCenterModal";
import * as Yup from "yup";
import {
  formatAccountPlanAssignmentMode,
  formatAccountPlanPriority,
  formatAccountPlanReminder,
  formatAccountPlanScheduleSummary,
} from "../../utils/accountPlanLabels";

const FIELD_CLASS =
  "w-full h-11 px-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none transition focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15 disabled:bg-slate-50";
const TEXTAREA_CLASS =
  "w-full min-h-[92px] px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none transition focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15 resize-y disabled:bg-slate-50";
const LABEL_CLASS = "block text-[11px] font-medium text-slate-400 mb-1.5 uppercase tracking-wide";

export const EMPTY_ACCOUNT_PLAN_TYPE_FORM = {
  name: "",
  description: "",
  frequency: "MONTHLY",
  installmentCount: "12",
  assignmentMode: "AUTO",
  defaultAssignedTo: "",
  reminderDaysBefore: "0",
  ticketPriority: "MEDIUM",
  remarksTemplate: "",
  active: true,
};

export const buildAccountPlanTypeInitialValues = (planType) => {
  if (!planType) {
    return { ...EMPTY_ACCOUNT_PLAN_TYPE_FORM };
  }
  return {
    name: planType.name || "",
    description: planType.description || "",
    frequency: planType.frequency || "MONTHLY",
    installmentCount: String(planType.installmentCount || ""),
    assignmentMode: planType.assignmentMode || "AUTO",
    defaultAssignedTo: planType.defaultAssignedTo ? String(planType.defaultAssignedTo) : "",
    reminderDaysBefore: String(planType.reminderDaysBefore ?? 0),
    ticketPriority: planType.ticketPriority || "MEDIUM",
    remarksTemplate: planType.remarksTemplate || "",
    active: planType.active !== false,
  };
};

export const buildAccountPlanTypePayload = (values) => ({
  name: values.name.trim(),
  description: values.description.trim() || null,
  frequency: values.frequency,
  installmentCount: values.frequency === "ONE_TIME" ? 1 : Number(values.installmentCount),
  assignmentMode: values.assignmentMode,
  defaultAssignedTo:
    values.assignmentMode === "MANUAL" && values.defaultAssignedTo
      ? Number(values.defaultAssignedTo)
      : null,
  reminderDaysBefore: Number(values.reminderDaysBefore),
  ticketPriority: values.ticketPriority,
  remarksTemplate: values.remarksTemplate.trim() || null,
  active: Boolean(values.active),
});

const buildValidationSchema = (t) =>
  Yup.object({
    name: Yup.string().trim().required(t("ccAccountPlanTypeNameRequired")),
    description: Yup.string().max(2000),
    frequency: Yup.string().required(t("ccAccountPlanFrequencyRequired")),
    installmentCount: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .typeError(t("ccInvalidNumber"))
      .when("frequency", {
        is: "ONE_TIME",
        then: (schema) => schema.oneOf([1]).required(),
        otherwise: (schema) => schema.min(1).max(120).required(t("ccAccountPlanInstallmentCountRequired")),
      }),
    assignmentMode: Yup.string().required(),
    defaultAssignedTo: Yup.string(),
    reminderDaysBefore: Yup.string().required(),
    ticketPriority: Yup.string().required(t("ccPriorityRequired")),
    remarksTemplate: Yup.string(),
    active: Yup.boolean(),
  });

const FormField = ({ label, required, error, touched, className = "", children }) => (
  <div className={className}>
    <label className={LABEL_CLASS}>
      {label}
      {required ? <span className="text-red-500"> *</span> : null}
    </label>
    {children}
    {error && touched ? <p className="mt-1 text-xs text-red-500">{error}</p> : null}
  </div>
);

const fieldErrorClass = (error, touched) => (error && touched ? "border-red-500" : "");

const FormSection = ({ title, children }) => (
  <section className="space-y-4">
    <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400">{title}</h4>
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>
  </section>
);

const AccountPlanTypeFormModal = ({
  open,
  editingPlanType,
  metadata,
  reminderOptions,
  agentOptions,
  saving,
  onClose,
  onSubmit,
  t,
}) => {
  const isEditing = Boolean(editingPlanType);
  const initialValues = useMemo(
    () => buildAccountPlanTypeInitialValues(editingPlanType),
    [editingPlanType, open]
  );
  const validationSchema = useMemo(() => buildValidationSchema(t), [t]);

  return (
    <CallCenterModal
      open={open}
      onClose={onClose}
      blockClose={saving}
      ariaLabelledBy="account-plan-type-form-title"
      eyebrow={isEditing ? t("ccAccountPlanTypeEditTitle") : t("ccAccountPlanTypeCreateTitle")}
      title={isEditing ? t("ccAccountPlanTypeEditTitle") : t("ccAccountPlanTypeCreateTitle")}
      subtitle={t("ccAccountPlanTypeFormHint")}
      maxWidth="2xl"
      maxHeight="90vh"
      closeLabel={t("ccClose")}
      contentVariant="none"
      bodyClassName="min-h-0 flex-1 overflow-hidden p-0"
    >
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={onSubmit}
          >
            {({ values, errors, touched, setFieldValue }) => {
              const isOneTime = values.frequency === "ONE_TIME";
              const cannotDeactivate =
                isEditing &&
                editingPlanType?.active &&
                !values.active &&
                Number(editingPlanType?.activeAccountPlanCount || 0) > 0;

              const previewPlanType = {
                frequency: values.frequency,
                installmentCount: isOneTime ? 1 : Number(values.installmentCount) || 0,
              };

              return (
                <Form className="flex min-h-0 flex-1 flex-col">
                  <h3 id="account-plan-type-form-title" className="sr-only">
                    {isEditing ? t("ccAccountPlanTypeEditTitle") : t("ccAccountPlanTypeCreateTitle")}
                  </h3>
                  <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 space-y-6">
                    <div className="rounded-xl border border-[#2B7FFF]/20 bg-[#2B7FFF]/5 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-[#1d4ed8]">
                        {t("ccAccountPlanTypeFormPreviewTitle")}
                      </p>
                      <p className="mt-1 text-sm font-medium text-slate-800">
                        {formatAccountPlanScheduleSummary(previewPlanType, metadata, t)}
                      </p>
                      <p className="mt-1 text-xs text-slate-600">
                        {t("ccAccountPlanTypeFormPreviewBody")
                          .replace("{reminder}", formatAccountPlanReminder(values.reminderDaysBefore, metadata))
                          .replace(
                            "{assignment}",
                            formatAccountPlanAssignmentMode(values.assignmentMode, metadata, t)
                          )
                          .replace("{priority}", formatAccountPlanPriority(values.ticketPriority, metadata))}
                      </p>
                    </div>

                    <FormSection title={t("ccAccountPlanTypeFormSectionBasic")}>
                      <FormField
                        label={t("ccAccountPlanTypeName")}
                        required
                        error={errors.name}
                        touched={touched.name}
                        className="md:col-span-2"
                      >
                        <Field
                          name="name"
                          className={`${FIELD_CLASS} ${fieldErrorClass(errors.name, touched.name)}`}
                        />
                      </FormField>

                      <FormField
                        label={t("ccAccountPlanTypeDescription")}
                        error={errors.description}
                        touched={touched.description}
                        className="md:col-span-2"
                      >
                        <Field as="textarea" name="description" rows={2} className={TEXTAREA_CLASS} />
                      </FormField>
                    </FormSection>

                    <FormSection title={t("ccAccountPlanTypeFormSectionSchedule")}>
                      <FormField
                        label={t("ccAccountPlanFrequency")}
                        required
                        error={errors.frequency}
                        touched={touched.frequency}
                      >
                        <Field
                          as="select"
                          name="frequency"
                          className={`${FIELD_CLASS} ${fieldErrorClass(errors.frequency, touched.frequency)}`}
                          onChange={(event) => {
                            const next = event.target.value;
                            setFieldValue("frequency", next);
                            if (next === "ONE_TIME") setFieldValue("installmentCount", "1");
                          }}
                        >
                          {(metadata?.frequencies || []).map((option) => (
                            <option key={option.code} value={option.code}>
                              {option.label}
                            </option>
                          ))}
                        </Field>
                      </FormField>

                      <FormField
                        label={t("ccAccountPlanInstallmentCount")}
                        required
                        error={errors.installmentCount}
                        touched={touched.installmentCount}
                      >
                        <Field
                          name="installmentCount"
                          type="number"
                          min="1"
                          max={isOneTime ? 1 : 120}
                          disabled={isOneTime}
                          className={`${FIELD_CLASS} ${fieldErrorClass(errors.installmentCount, touched.installmentCount)}`}
                        />
                        {isOneTime ? (
                          <p className="mt-1 text-xs text-slate-500">{t("ccAccountPlanOneTimeNote")}</p>
                        ) : null}
                      </FormField>

                      <FormField label={t("ccAccountPlanReminderOffset")} className="md:col-span-2">
                        <Field as="select" name="reminderDaysBefore" className={FIELD_CLASS}>
                          {reminderOptions.map((option) => (
                            <option key={option.daysBefore} value={String(option.daysBefore)}>
                              {option.label}
                            </option>
                          ))}
                        </Field>
                      </FormField>
                    </FormSection>

                    <FormSection title={t("ccAccountPlanTypeFormSectionTickets")}>
                      <FormField label={t("ccAccountPlanAssignmentMode")}>
                        <Field
                          as="select"
                          name="assignmentMode"
                          className={FIELD_CLASS}
                          onChange={(event) => {
                            const next = event.target.value;
                            setFieldValue("assignmentMode", next);
                            if (next !== "MANUAL") setFieldValue("defaultAssignedTo", "");
                          }}
                        >
                          {(metadata?.assignmentModes || []).map((option) => (
                            <option key={option.code} value={option.code}>
                              {option.label}
                            </option>
                          ))}
                        </Field>
                      </FormField>

                      <FormField
                        label={t("ccAccountPlanTicketPriority")}
                        required
                        error={errors.ticketPriority}
                        touched={touched.ticketPriority}
                      >
                        <Field
                          as="select"
                          name="ticketPriority"
                          className={`${FIELD_CLASS} ${fieldErrorClass(errors.ticketPriority, touched.ticketPriority)}`}
                        >
                          {(metadata?.priorities || []).map((option) => (
                            <option key={option.code} value={option.code}>
                              {option.label}
                            </option>
                          ))}
                        </Field>
                      </FormField>

                      {values.assignmentMode === "MANUAL" ? (
                        <FormField label={t("ccAccountPlanTypeDefaultAgent")} className="md:col-span-2">
                          <Field as="select" name="defaultAssignedTo" className={FIELD_CLASS}>
                            <option value="">{t("ccAccountPlanTypeDefaultAgentOptional")}</option>
                            {agentOptions.map((agent) => (
                              <option key={agent.userId} value={String(agent.userId)}>
                                {agent.name}
                              </option>
                            ))}
                          </Field>
                          <p className="mt-1 text-xs text-slate-500">{t("ccAccountPlanTypeDefaultAgentHint")}</p>
                        </FormField>
                      ) : null}

                      <FormField label={t("ccAccountPlanRemarksTemplate")} className="md:col-span-2">
                        <Field as="textarea" name="remarksTemplate" rows={3} className={TEXTAREA_CLASS} />
                      </FormField>
                    </FormSection>

                    {isEditing ? (
                      <section className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                          <Field type="checkbox" name="active" disabled={cannotDeactivate} />
                          {t("ccAccountPlanTypeActive")}
                        </label>
                        {cannotDeactivate ? (
                          <p className="text-xs text-amber-700">{t("ccAccountPlanTypeDeactivateBlocked")}</p>
                        ) : null}
                        {Number(editingPlanType?.totalAccountPlanCount || 0) > 0 ? (
                          <p className="text-xs text-slate-500">
                            {t("ccAccountPlanTypeUsageSummary").replace(
                              "{count}",
                              String(editingPlanType.totalAccountPlanCount)
                            )}
                          </p>
                        ) : null}
                      </section>
                    ) : null}
                  </div>

                  <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
                    <button
                      type="button"
                      onClick={onClose}
                      className="h-9 rounded-xl border border-slate-200 px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                    >
                      {t("ccCancel")}
                    </button>
                    <button
                      type="submit"
                      disabled={saving || cannotDeactivate}
                      className="h-9 rounded-xl bg-[#2B7FFF] px-4 text-sm font-semibold text-white hover:bg-[#1d6ef2] disabled:opacity-50"
                    >
                      {saving ? t("ccSaving") : t("ccSave")}
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
    </CallCenterModal>
  );
};

export default AccountPlanTypeFormModal;
